import React, { useState, useContext } from "react";
import { BaseRoundButton } from "@/shared/components/button/base-round-button";
import { LoadingModalContext } from "@/shared/components/loading/loading-context";
import { MoneyLinkWarning } from "@/shared/utils/custom-exception";
import { ErrorDialogContext } from "@/shared/context/error-context";


type FileUploaderProps = {
  onFilesSelected: (files: File[]) => void; // ★ モーダル側に通知可能
  accept?: string;
  multiple?: boolean;
  maxFiles?: number;
  maxFileSizeMB?: number;  // 1ファイルあたりの上限 (MB)
  maxTotalSizeMB?: number; // 合計サイズ上限（MB）
  files?: File[];
};

// acceptから拡張子リストを作成
const acceptList = (accept: string): string[] => {
  if (!accept) return [];
  return accept.split(",").map(ext => ext.trim().toLowerCase())
  .map(ext => {
    if (ext.startsWith(".")) return ext.slice(1);
    if (ext === "image/jpg") return ["jpg"];
    if (ext === "image/jpeg") return ["jpeg"];
    if (ext === "image/png") return ["png"];
    if (ext === "image/heic") return ["heic"];
    if (ext === "application/pdf") return ["pdf"];
    if (ext === "application/vnd.ms-excel") return ["xls"];
    if (ext === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet") return ["xlsx"];
    if (ext === "text/csv" || ext === "application/csv") return ["csv"];
    return null;
  })
  .flat()
  .filter(Boolean) as string[];
};

export const FileUploader: React.FC<FileUploaderProps> = ({
  onFilesSelected,
  accept = "*/*",
  multiple = true,
  maxFiles = 10,
  maxFileSizeMB,
  maxTotalSizeMB,
  files: externalFiles, 
}) => {
  const [internalFiles, setInternalFiles] = useState<File[]>([]);
  const [isDragActive, setIsDragActive] = useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const acceptedExtensions = acceptList(accept);
  const uniqueAcceptedExtensions = Array.from(new Set(acceptedExtensions));
  const { isLoading } = useContext(LoadingModalContext);
    // グローバルエラーハンドリング
  const { setError } = useContext(ErrorDialogContext);

  const files = externalFiles !== undefined ? externalFiles : internalFiles;

  const updateFiles = (newFiles: File[]) => {
    onFilesSelected(newFiles);
    if (externalFiles === undefined) {
      setInternalFiles(newFiles);
    }
  };

  // エクスプローラーからのファイル選択・ドロップ共通処理
  const handleFiles = (selected: File[]) => {
    // 重複ファイルのチェック
    const duplicateFiles = selected.filter(file => 
      files.some(f => f.name === file.name && f.size === file.size)
    );

    if (duplicateFiles.length > 0) {
      setError(new MoneyLinkWarning({
        message: `以下のファイルは既に選択されています。\n${duplicateFiles.map(f => f.name).join("\n")}`
      }));
      return;
    }

    // 複数回ファイル保存に対応するため、既存ファイル数と合わせてチェック
    const newFiles = [...files, ...selected].filter(
      (file,idx, arr) => arr.findIndex(f => f.name === file.name && f.size === file.size) === idx
    );

    // 新規追加分のチェック
    const newAddedFiles = selected.filter(
      file => !files.some(f => f.name === file.name && f.size === file.size)
    );

    // 拡張子チェック
    if (acceptedExtensions.length > 0) {
      const invalidFiles = newAddedFiles.filter(file => {
        const ext = file.name.split(".").pop()?.toLowerCase();
        return !ext || !acceptedExtensions.includes(ext);
      });
      if (invalidFiles.length > 0) {
        setError(new MoneyLinkWarning({
          message: `許可されていないファイル形式です\n${invalidFiles.map(f => f.name).join("\n")}`
        }));
        return;
      }
    }

    if (newFiles.length > maxFiles) {
      setError(new MoneyLinkWarning({
        message: `最大${maxFiles}ファイルまで選択可能です。`
      }));
      return;
    }
    if (maxFileSizeMB) {
      const maxFileSizeBytes = maxFileSizeMB * 1024 * 1024;
      for (const file of selected) {
        if (file.size > maxFileSizeBytes) {
          setError(new MoneyLinkWarning({
            message: `ファイル「${file.name}」はサイズが${maxFileSizeMB}MBを超えています。`
          }));
          return;
        }
      }
    }
    if (maxTotalSizeMB) {
      const maxTotalSizeBytes = maxTotalSizeMB * 1024 * 1024;
      const totalSize = newFiles.reduce((sum, file) => sum + file.size, 0);
      if (totalSize > maxTotalSizeBytes) {
        setError(new MoneyLinkWarning({
          message: `ファイルの合計サイズ上限は${maxTotalSizeMB}MBです。`
        }));
        return;
      }
    }
    updateFiles(newFiles);
  };

  // ファイル選択
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    handleFiles(Array.from(e.target.files));
    e.target.value = ""; // 同じファイルを再選択できるようにするため，input内部で保持している値をリセット
  };

  // ドロップ
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragActive(false);
    if (isLoading) return; // Loading中はドロップを受け付けない

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(Array.from(e.dataTransfer.files));
      e.dataTransfer.clearData();
    }
  };

  // ドラッグ中
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (isLoading) return; // Loading中はドラッグを受け付けない
    setIsDragActive(true);
  };

  // ドラッグしない
  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragActive(false);
  };

  // ファイルの取消
  const handleRemoveFile = (index: number) => {
    if (isLoading) return;
    const newFiles = files.filter((_, i) => i !== index);
    updateFiles(newFiles);
  };

  // ボタンクリックでファイル選択ダイアログ表示
  const handleButtonClick = () => {
    if (isLoading) return;
    inputRef.current?.click();
  };

  return (
    <div
      className="file-upload"
      id="dropArea"
      onDrop={handleDrop}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onClick={handleButtonClick}
    >
      <div className="uploaded-files-list">
          <p className="file-text">
            この枠内にファイルをドラッグ＆ドロップするか<br />
            ボタンを押してファイルを選択してください<br />
            {uniqueAcceptedExtensions.length > 0
              ? `許可されている拡張子：${uniqueAcceptedExtensions.map(ext => `${ext}`).join(", ")}`
              : "許可されている拡張子：すべて"}
          </p>
          <BaseRoundButton
            buttonText="ファイルを選択"
            className="file-btn"
            onClick={e => {
              e.stopPropagation();
              handleButtonClick();
            }}
            loadingNegative={true}
          />
          {files.length === 0 ? (
            <ul className="file-list is-empty">
              <li>ファイルが選択されていません</li>
            </ul>
          ) : (
            <ul className="file-list">
              {files.map((f, i) => (
                <li
                  key={i}
                  style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px", wordBreak: "break-all" }}
                >
                  <span style={{ flex: 1 }}>{f.name}</span>
                  <BaseRoundButton
                    buttonText="×"
                    onClick={e => {
                      e.stopPropagation();
                      handleRemoveFile(i);
                    }}
                    loadingNegative={true}
                  />
                </li>
              ))}
            </ul>
          )}
          <input
            ref={inputRef}
            type="file"
            id="fileInput"
            accept={accept}
            multiple={multiple}
            style={{ display: "none" }}
            onChange={handleChange}
          />
      </div>
    </div>
  );
};
